<script lang="ts">
import { AgreementInfo } from '@/modules/agreements/models';
import { computed, onMounted, shallowRef, watchEffect } from 'vue';
import {
  AgreementStatus,
  CollateralType,
  SettlementType,
  Side,
  agreementsService,
  formatCollateralType,
  formatSettlementType,
} from '@/connect';
import logger from '@/modules/common/services/logger.service';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';
import i18n from '@/localisation/i18n';
import { Security } from '@/modules/common/models';
import { ColDef } from 'ag-grid-enterprise';
import * as cols from '@/modules/common/components/ag-table/columns/agreements';
import { checkbox, rowDrag } from '@/modules/common/components/ag-table/columns/common';
import { AgTableClient } from '@/modules/common/components/ag-table';
import { useCompaniesStore } from '@/store/companies-store';
import { formatCompanyInfo } from '@/modules/agreements/utils';
import { store as vuex } from '@/store/store';
import CounterpartyBreakdown from '@/modules/agreements/components/CounterpartyBreakdown.vue';

const settlementTypeFilters = Object.values(SettlementType).filter(
  (v): v is SettlementType => typeof v === 'number' && v !== SettlementType.UNSPECIFIED
);

const collateralTypeFilters = Object.values(CollateralType).filter(
  (v): v is CollateralType =>
    typeof v === 'number' &&
    ![
      CollateralType.COLLATERAL_TYPE_UNSPECIFIED,
      CollateralType.NON_CASH_JGB,
      CollateralType.NON_CASH_EQ,
    ].includes(v)
);
</script>

<script setup lang="ts">
import { watch } from 'vue';

const snackbarManager = useSnackbarManager();
const { getCompany } = useCompaniesStore();

const props = withDefaults(
  defineProps<{
    value: AgreementInfo[];
    statuses?: AgreementStatus[];
    side?: Side;
    security?: Security | null;
    companyIdFilter?: string;
    useDefaultAgreements?: boolean;
    showCounterpartyBreakdown?: boolean;
  }>(),
  {
    statuses: () => [AgreementStatus.ACTIVE],
    side: undefined,
    security: undefined,
    companyIdFilter: undefined,
    useDefaultAgreements: false,
    showCounterpartyBreakdown: false,
  }
);

const emit = defineEmits<{
  (e: 'input', value: AgreementInfo[]): void;
  (e: 'agreement-ids-eligible', agreementIds: Set<string>): void;
  (e: 'ready', value: boolean): void;
}>();

const agreements = shallowRef<AgreementInfo[]>([]);
const agreementIdsEligible = shallowRef(new Set<string>());
const selectedAgreementIds = computed(() => new Set(props.value.map((a) => a.id)));

watchEffect(() => {
  agreementIdsEligible.value = new Set(agreements.value.map((agreement) => agreement.id));
  emit('agreement-ids-eligible', agreementIdsEligible.value);
});

const searchTerms = shallowRef<string[]>([]);
const activeFilters = shallowRef(new Set<string>());

function selectFilter(column: keyof AgreementInfo, type: SettlementType | CollateralType) {
  const filterKey = `${column}:${type}`;
  const newFilters = new Set(activeFilters.value);

  if (newFilters.has(filterKey)) {
    newFilters.delete(filterKey);
  } else {
    newFilters.add(filterKey);
  }

  activeFilters.value = newFilters;
}

// Computed property that filters agreements based on active chip filters and search terms
const filteredAgreements = computed(() => {
  let filtered = agreements.value;

  // Apply chip filters (settlement type, collateral type)
  if (activeFilters.value.size > 0) {
    const arrFiltered = Array.from(activeFilters.value);

    // Group filters by column (e.g., settlementType, collateralType)
    const filtersByColumn = new Map<string, Set<number>>();
    arrFiltered.forEach((filterKey) => {
      const [column, strType] = filterKey.split(':');
      const type = parseInt(strType, 10);
      let filters = filtersByColumn.get(column);
      if (filters === undefined) {
        filters = new Set();
        filtersByColumn.set(column, filters);
      }
      filters.add(type);
    });

    // Filter agreements:
    filtered = filtered.filter((agreement) =>
      Array.from(filtersByColumn.entries()).every(([column, types]) => {
        const agreementValue = agreement[column as keyof AgreementInfo];
        // OR logic: agreement matches if its value is ANY of the selected types
        return types.has(agreementValue as number);
      })
    );
  }

  // Apply search term filters
  if (searchTerms.value.length > 0) {
    filtered = filtered.filter((agreement) => {
      // For each agreement, check if ALL search terms match at least one field
      return searchTerms.value.every((term) => {
        const lowerTerm = term.toLowerCase();

        if (agreement.shortName?.toLowerCase().includes(lowerTerm)) {
          return true;
        }
        const company = agreement.counterpartyId ? getCompany(agreement.counterpartyId) : null;
        const formattedCompanyInfo = company ? formatCompanyInfo(company) : '';
        return formattedCompanyInfo.toLowerCase().includes(lowerTerm);
      });
    });
  }

  return filtered;
});

const settlementTypesString = computed(() => {
  const counts = new Map<SettlementType, number>();

  props.value.forEach((agreement) => {
    if (agreement.settlementType) {
      // Increment the count for this settlement type (default to 0 if not yet seen)
      counts.set(agreement.settlementType, (counts.get(agreement.settlementType) ?? 0) + 1);
    }
  });

  return Array.from(counts.entries())
    .map(([type, count]) => `${count} ${SettlementType[type]}`)
    .join(', ');
});

const columnDefs = computed<ColDef[]>(() => {
  const config = { filter: false };
  return [
    rowDrag(),
    checkbox(),
    cols.shortName(config),
    cols.counterpartyId(config),
    cols.collateralType(config),
    cols.settlementType(config),
  ];
});

function getRowId(node: AgreementInfo) {
  return node.id;
}

function handleRowDragEnd(reorderedItems: AgreementInfo[]) {
  // Only update if we actually have reordered items
  if (reorderedItems.length === 0) {
    return;
  }

  // reorder the selected items to match the new grid order
  if (props.value.length > 0) {
    const reorderedSelected = reorderedItems.filter((a) => selectedAgreementIds.value.has(a.id));
    emit('input', reorderedSelected);
  }
}

const queryConfig = computed(() => ({
  statuses: props.statuses ?? [AgreementStatus.ACTIVE],
  side: props.side,
  eligibleInstrument: props.security?.cusip,
  bookingAccounts: [],
  counterpartyBookingAccounts: [],
  counterpartyIds: [],
  tradeTypes: [],
  collateralCurrencies: [],
  deliveryTypes: [],
}));

// we want to fetch fresh data any time the queryConfig changes
watch(queryConfig, queryData, { deep: true });

async function queryData(): Promise<void> {
  try {
    const result = await agreementsService.queryAgreements(
      queryConfig.value,
      props.companyIdFilter
    );

    if (!result.success) {
      snackbarManager.error(i18n.t(result.error, result.details) as string);
      return;
    }

    const allAgreements = result.data.agreements.map(AgreementInfo.fromData);

    // Reorder agreements: selected ones at the top, then the rest
    const selectedAgreements = allAgreements.filter((a) => selectedAgreementIds.value.has(a.id));
    const unselectedAgreements = allAgreements.filter((a) => !selectedAgreementIds.value.has(a.id));

    agreements.value = [...selectedAgreements, ...unselectedAgreements];

    // apply default agreement selection logic if desired (create order and not match button)
    if (props.useDefaultAgreements) {
      const defaultAgreements = vuex.state.loginState?.user?.companyDefaultOmsAgreements;

      // If there are no defaults, select all agreements (only on initial load)
      if (!defaultAgreements || defaultAgreements.length === 0) {
        if (props.value.length === 0) {
          emit('input', agreements.value);
          return; // Don't normalize since we just set all
        }
      } else {
        // Check which defaults are in the current agreements list
        const applicableDefaults = agreements.value.filter((a) =>
          defaultAgreements.some((defaultAgreement) => defaultAgreement.id === a.id)
        );

        // If none of the defaults are in the current list, select all agreements
        if (applicableDefaults.length === 0) {
          emit('input', agreements.value);
          return; // Don't normalize since we just set all
        }

        // force applicable default agreements to the top of the list
        const orderingMap = new Map(
          applicableDefaults.map((agreement, index) => [agreement.id, index])
        );
        agreements.value = [...agreements.value].sort(
          (a, b) => (orderingMap.get(a.id) ?? Infinity) - (orderingMap.get(b.id) ?? Infinity)
        );

        // If some defaults are applicable, select those defaults
        emit('input', applicableDefaults);
        return; // Don't normalize since we just set the selection
      }
    }

    // Normalize selected agreements to reference the same object instances as the grid
    if (props.value.length > 0) {
      const normalizedSelected = agreements.value.filter((a) =>
        selectedAgreementIds.value.has(a.id)
      );
      emit('input', normalizedSelected);
    }
  } catch (e) {
    logger.warn(e);
  }
}

onMounted(async () => {
  await queryData();
  emit('ready', true);
});
</script>

<template>
  <div>
    <VCombobox
      v-model="searchTerms"
      append-icon=""
      chips
      clearable
      data-test-id="agreement-search-input"
      dense
      multiple
      outlined
      placeholder="Search"
      prepend-inner-icon="mdi-magnify"
    >
      <template #selection="{ attrs, item, parent, selected }">
        <v-chip v-bind="attrs" close :input-value="selected" @click:close="parent.selectItem(item)">
          {{ item }}
        </v-chip>
      </template>
    </VCombobox>
    <div style="display: flex; align-items: center; flex-wrap: wrap">
      <v-chip
        v-for="type in settlementTypeFilters"
        :key="'settlement-' + type"
        class="mb-2 mr-2"
        :color="activeFilters.has(`settlementType:${type}`) ? 'primary' : undefined"
        @click="selectFilter('settlementType', type)"
      >
        {{ formatSettlementType(type) }}
      </v-chip>
      <v-divider class="mr-2" style="height: 32px" vertical />
      <v-chip
        v-for="type in collateralTypeFilters"
        :key="'collateral-' + type"
        class="mb-2 mr-2"
        :color="activeFilters.has(`collateralType:${type}`) ? 'primary' : undefined"
        @click="selectFilter('collateralType', type)"
      >
        {{ formatCollateralType(type) }}
      </v-chip>
    </div>
    <AgTableClient
      auto-grow
      :column-defs="columnDefs"
      data-test-id="agreement-selector-table"
      fit-full-width
      :get-row-id="getRowId"
      no-menu
      no-move
      no-sort
      :row-data="filteredAgreements"
      row-drag
      :selected-items="value"
      table-id="agreement-selector-table"
      @update:row-order="handleRowDragEnd"
      @update:selected-items="emit('input', $event)"
    />
    <div v-if="showCounterpartyBreakdown">
      <p class="pt-4">
        You've selected {{ value.length }} of {{ agreements.length }} agreements.
        <br />
        <span v-if="value.length > 0">Includes {{ settlementTypesString }} settlements.</span>
      </p>
      <div v-if="value.length > 0">
        <v-divider class="my-4" />
        <p>
          Agreements by counterparty: <br />
          <CounterpartyBreakdown :agreements="value" :side="side" :statuses="statuses" />
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss"></style>
